import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { fetchContent, subscribeToType, LIVE_MODE } from '../lib/sanity'
import { TbClipboardList } from 'react-icons/tb'
import { FaUserDoctor } from 'react-icons/fa6'
import { MdOutlineLocalShipping } from 'react-icons/md'
import { IconType } from 'react-icons'

interface Step {
  title: string
  description: string
  iconType: string
}

interface HowItWorksContent {
  heading: string
  steps: Step[]
}

const iconMap: Record<string, IconType> = {
  questionnaire: TbClipboardList,
  medical: FaUserDoctor,
  shipping: MdOutlineLocalShipping,
}

const StepCard = ({ step, index }: { step: Step; index: number }) => {
  const Icon = iconMap[step.iconType]
  const imageOnRight = index % 2 === 0
  const imageOrder = imageOnRight ? 'md:order-2' : 'md:order-1'
  const textOrder = imageOnRight ? 'md:order-1' : 'md:order-2'

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="rounded-3xl border border-teal-100 bg-white/90 p-6 shadow-[0_15px_45px_rgba(6,96,94,0.1)]"
    >
      <div className="grid gap-6 md:grid-cols-2 md:items-center">
        <div className={`order-2 ${textOrder} space-y-3 lg:space-y-5`}>
          <div className="flex items-center gap-3 lg:gap-5">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#cdfbf1] text-lg font-semibold text-teal-800 lg:h-[4.75rem] lg:w-[4.75rem] lg:text-3xl">
              {String(index + 1).padStart(2, '0')}
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-teal-600 lg:text-base lg:tracking-[0.55em]">Step {index + 1}</p>
              <h3 className="text-2xl font-semibold text-slate-900 lg:text-[2.35rem] lg:leading-tight">{step.title}</h3>
            </div>
            {Icon && (
              <span className="ml-auto rounded-full bg-teal-50 p-3 text-teal-600 lg:p-5">
                <Icon className="h-5 w-5 lg:h-9 lg:w-9" />
              </span>
            )}
          </div>
          <p className="text-base text-slate-600 leading-relaxed lg:text-xl lg:leading-relaxed">{step.description}</p>
        </div>
        <div className={`order-1 ${imageOrder}`}>
          <div className="overflow-hidden rounded-2xl border border-teal-100 bg-white">
            <img
              src={`/images/step-${index + 1}.webp`}
              alt={`${step.title} illustration`}
              className="h-56 w-full object-cover md:h-64"
            />
          </div>
        </div>
      </div>
    </motion.div>
  )
}

const HowItWorks = () => {
  const [content, setContent] = useState<HowItWorksContent | null>(null)

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    const getContent = async () => {
      try {
        const [data] = await fetchContent('howItWorks')
        setContent(data)
        if (LIVE_MODE) {
          unsubscribe = subscribeToType('howItWorks', (docs) => setContent(docs[0]))
        }
      } catch (error) {
        console.error('Error fetching How It Works content:', error)
      }
    }
    getContent()
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [])

  const defaultSteps = [
    {
      title: 'Start with your goals',
      description: 'Share the routines, priorities, and accountability support that would make your next steps feel practical.',
      iconType: 'questionnaire',
    },
    {
      title: 'Coordinate your care',
      description: 'Bring clinical questions and medication decisions to your licensed healthcare professional while Andrea supports your routines.',
      iconType: 'medical',
    },
    {
      title: 'Keep the rhythm',
      description: 'Use remote check-ins, reflection, and small lifestyle practices to stay accountable over time.',
      iconType: 'shipping',
    },
  ]

  const steps = content?.steps || defaultSteps

  return (
    <section id="how-it-works-section" className="relative isolate overflow-hidden bg-[#cdfbf1] py-20 text-slate-900">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.9),_rgba(205,251,241,0.6))]" />
      <div className="container relative z-10 mx-auto px-6">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.5em] text-teal-600">Your concierge path</p>
          <h2 className="mt-3 text-4xl font-semibold text-slate-900">
            {content?.heading || 'How it works'}
          </h2>
          <p className="mt-4 text-base text-slate-600">
            Everything is paced so you can move through onboarding comfortably and stay focused on sustainable routines.
          </p>
        </div>

        <div className="mt-14 space-y-10">
          {steps.map((step, index) => (
            <StepCard key={`${step.title}-${index}`} step={step} index={index} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default HowItWorks
